import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FiArrowLeft, FiExternalLink, FiCode, FiChevronDown, FiCheck, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import ShieldCheckIcon from '../../ShieldCheckIcon';
import Toggle from '../../Toggle';
import { toast } from '../../Toast';
import { WidgetDef, SettingControl, SettingValues, AddBlock, settingDefaults, ALREADY_LOADED } from './types';
import { CodeBox, useWidgetSite } from './shared';

// one widget (specs 621:4886 … 698:29741): header, meta bar, saved codes, live demo, Add It to a Page, Settings.
// Preview only: nothing reads the website or saves a code yet.
const WidgetDetail: React.FC<{ widget: WidgetDef }> = ({ widget: w }) => {
    const history = useHistory();
    const { host } = useWidgetSite();
    const [settings, setSettings] = useState<SettingValues>(() => settingDefaults(w.settings));
    const [saved, setSaved] = useState(w.savedCodeSelected ?? (w.savedCodes ? w.savedCodes.length - 1 : 0));
    const addRef = useRef<HTMLElement>(null);
    const set = (key: string, value: string | boolean) => setSettings(s => ({ ...s, [key]: value }));

    const summary = w.installed
        ? w.summary ?? `On ${w.page} · ${w.views?.toLocaleString('en-AU')} views in 30 days`
        : 'Not on the website yet';

    const generate = () => toast.info('Preview only: the code was not saved');

    return <div className="widget-detail-v2">
        <button type="button" className="link back" onClick={() => history.push('/client-settings/widgets/list')}><FiArrowLeft /> Back to Widgets</button>

        <div className="page-header">
            <div>
                <h1>{w.title}</h1>
                <div className="wd-summary">
                    <span className={`badge ${w.installed ? 'ok' : 'bad'}`}>{w.installed ? 'Installed' : 'Not installed'}</span>
                    <span>{summary}</span>
                </div>
            </div>
            <div className="action-top">
                <a className="btn-outline with-icon" href={`https://${host}${w.page ?? '/'}`} target="_blank" rel="noopener noreferrer"><FiExternalLink /> View on the Website</a>
                {w.getCodeButton && <button type="button" className="btn-primary with-icon" onClick={() => addRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}><FiCode /> Get the Code</button>}
            </div>
        </div>

        <div className="wd-meta">
            <div><small>{w.metaLabel ?? 'Page'}</small><strong>{w.page ?? 'not on the website yet'}</strong></div>
            <div><small>Last checked</small><strong>5 minutes ago</strong></div>
            <div><small>Views (30 days)</small><strong>{w.views === null ? '—' : w.views.toLocaleString('en-AU')}</strong></div>
            <span className="sp" />
            <span className="seen"><FiCheckCircle /> {w.metaStatus ?? 'Investi can see it on the page'}</span>
        </div>

        {w.savedCodes && <div className="wd-saved">
            <span className="label">Saved codes</span>
            <div className="pills">
                {w.savedCodes.map((c, i) => <button key={c.path} type="button" className={i === saved ? 'active' : ''} onClick={() => setSaved(i)}>
                    <code>{c.path}</code> <span>· {c.note}</span>
                </button>)}
            </div>
        </div>}

        <div className="wd-detail-row">
            <div className="wd-demo">
                <div className="browser-bar">
                    <span className="dot" /><span className="dot" /><span className="dot" />
                    <span className="url"><ShieldCheckIcon /> <code>{host}{w.demoPath}</code></span>
                    <span className="live">Live demo</span>
                </div>
                <div className="widget"><w.Demo settings={settings} /></div>
            </div>

            <div className="wd-side">
                <Collapsible title="Add It to a Page" innerRef={addRef}>
                    {w.addNote && <div className="wd-green-note dot">{w.addNote}</div>}
                    {w.addBlocks.map((b, i) => <AddBlockView key={i} block={b} />)}
                    <div className="wd-green-note"><FiCheck /> {w.addFooter ?? ALREADY_LOADED}</div>
                </Collapsible>

                {w.Side && <w.Side />}

                {w.settings && <Collapsible title="Settings" className="wd-settings">
                    {w.settings.map((c, i) => <SettingRow key={i} control={c} settings={settings} set={set} />)}
                    <div className="generate">
                        <hr />
                        <button type="button" className="btn-generate" onClick={generate}>Generate the Code</button>
                        {w.generateNote && <p>{w.generateNote}</p>}
                    </div>
                </Collapsible>}
            </div>
        </div>

        {w.After && <w.After />}
    </div>;
};

const Collapsible: React.FC<{ title: string; className?: string; innerRef?: React.Ref<HTMLElement> }> = ({ title, className, innerRef, children }) => {
    const [open, setOpen] = useState(true);
    return <section ref={innerRef} className={`card wd-collapsible${open ? '' : ' closed'}${className ? ` ${className}` : ''}`}>
        <button type="button" className="acc-head" aria-expanded={open} onClick={() => setOpen(!open)}>
            <h2>{title}</h2> <FiChevronDown className="chev" />
        </button>
        {open && children}
    </section>;
};

const AddBlockView: React.FC<{ block: AddBlock }> = ({ block: b }) => <>
    <div className="wd-label">{b.title}</div>
    {'code' in b && <CodeBox code={b.code} dark={b.dark} />}
    {'codes' in b && <div className="wd-mini-codes">
        {b.codes.map(c => <MiniCode key={c.code} label={c.label} code={c.code} />)}
    </div>}
    {'text' in b && <p className="wd-muted">{b.text}</p>}
    {'options' in b && <div className="wd-options">
        {b.options.map(o => <div key={o.title}><FiCode /> <span><strong>{o.title}</strong><small>{o.hint}</small></span></div>)}
    </div>}
    {'after' in b && b.after && <p className="wd-muted">{b.after}</p>}
</>;

// a small navy code with its label above; click to copy
export const MiniCode: React.FC<{ label: string; code: string }> = ({ label, code }) =>
    <button type="button" className="wd-mini-code" title="Copy" onClick={() => {
        navigator.clipboard?.writeText(code).then(() => toast.success(`Copied the ${label} code`), () => toast.error('Could not copy — select it and copy it by hand'));
    }}>
        <small>{label}</small>
        <code>{code}</code>
    </button>;

const SettingRow: React.FC<{ control: SettingControl; settings: SettingValues; set: (key: string, value: string | boolean) => void }> = ({ control: c, settings, set }) => {
    if (c.kind === 'heading') return <div className="wd-label">{c.label}</div>;
    if (c.kind === 'note') return <div className="wd-info"><FiAlertCircle /> {c.text}</div>;
    if (c.kind === 'custom') return <c.render settings={settings} set={set} />;
    if (c.kind === 'toggle')
        return <div className="wd-setting">
            <div className="t"><span>{c.label}</span>{c.hint && <small>{c.hint}</small>}</div>
            <Toggle label={c.label} checked={!!settings[c.key]} onChange={v => set(c.key, v)} />
        </div>;
    if (c.kind === 'segmented') {
        const disabled = c.disabled ? c.disabled(settings) : false;
        const seg = <div className={`wd-segmented${disabled ? ' disabled' : ''}`} role="radiogroup" aria-label={c.label ?? c.key}>
            {c.options.map(o => <button key={o} type="button" role="radio" aria-checked={settings[c.key] === o} disabled={disabled}
                className={settings[c.key] === o ? 'active' : ''} onClick={() => set(c.key, o)}>{o}</button>)}
        </div>;
        if (c.inline) return <div className="wd-setting inline"><div className="t"><span>{c.label}</span></div>{seg}</div>;
        return <>{c.label && <div className="wd-field-label">{c.label}</div>}{seg}</>;
    }
    if (c.kind === 'text')
        return <label className="wd-text">
            <span>{c.label}</span>
            {c.multiline
                ? <textarea rows={2} value={String(settings[c.key] ?? '')} onChange={e => set(c.key, e.target.value)} />
                : <input value={String(settings[c.key] ?? '')} onChange={e => set(c.key, e.target.value)} />}
        </label>;
    if (c.kind === 'select')
        return <div className="wd-setting inline">
            <div className="t"><span>{c.label}</span></div>
            <select className="wd-select" aria-label={c.label} value={String(settings[c.key])} onChange={e => set(c.key, e.target.value)}>
                {c.options.map(o => <option key={o}>{o}</option>)}
            </select>
        </div>;
    // colour: swatch + hex input
    return <div className="wd-color">
        {c.label && <div className="wd-label">{c.label}</div>}
        <div className="row">
            <input type="color" value={String(settings[c.key])} onChange={e => set(c.key, e.target.value.toUpperCase())} aria-label={c.label ?? c.key} />
            <input className="hex" value={String(settings[c.key])} onChange={e => set(c.key, e.target.value)} />
        </div>
        {c.hint && <small>{c.hint}</small>}
    </div>;
};

export default WidgetDetail;
